import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import 'ag-grid-enterprise'
import { useQuery } from '@tanstack/react-query'
import ReactSelect from 'react-select'
import { getLocaleText } from '../../actions/datagrid'
//import { deleteDiscussion } from '../../actions/discussions/discussiondelete'
import { getDiscussion } from '../../actions/discussions/discussionget'
import { getAllDiscussions } from '../../actions/discussions/discussionsgetall'
import { getPersonDiscussions } from '../../actions/discussions/discussionsgetallforperson'
import { currentUserPersonQuery, discussionModelsGetAll } from '../../api'
import { Button } from '../../common/Button.tsx'
import Chip from '../../common/chip/Chip.jsx'
import {
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../common/grid'
import { IconAddNew, pencilIconString, printerIconString, restoreIconString } from '../../common/icons'
import { LoadingSuspense } from '../../common/loaders/loadingsuspense'
import { reactSelectSearchSettings, zIndexFix } from '../../common/reactselect'
import { dateFormatter, dateGetter, jsError, localDate } from '../../common/utils'
import { actionTypeSelector, discussionSelector, discussionsSelector } from '../../reducers'
import { PdfDiscussion } from '../contracts/pdfdiscussion'
import { DiscussionModalBody } from './modal/discussionmodalbody'

const statusClassMap = {
	OPEN: 'warning',
	DONE: 'success',
	LATE: 'error',
}

const filteredColumn = 'discussionModel.discussionModelName'
const viewStateKey = 'discussionViewState'

export default function DiscussionView({ person }) {
	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const { t } = useTranslation()
	const dispatch = useDispatch()

	const statusMap = {
		OPEN: t('Open', 'Avoinna'),
		DONE: t('Done', 'Pidetty'),
		LATE: t('Late', 'Myöhässä'),
	}

	const discussions = useSelector(discussionsSelector)
	const discussion = useSelector(discussionSelector)
	const gridRef = useRef(null)
	const modelSelectInputRef = useRef()

	const [pdf, setPdf] = useState(null)

	const currentUserPerson = useQuery(currentUserPersonQuery(isPersonal)).data
	const personId = isPersonal ? currentUserPerson?.personId : person?.personId

	useEffect(() => {
		if (!isPersonal && !person) {
			dispatch(getAllDiscussions())
		}
		return () =>
			dispatch({
				type: 'CLEAR_DISCUSSIONS',
				payload: { discussions: null },
			})
	}, [isPersonal, person, dispatch])

	useEffect(() => {
		if (personId) {
			dispatch(getPersonDiscussions(personId))
		}
	}, [personId, dispatch])

	function createModelColumns(modelName) {
		const modelColumns = [
			{
				headerName: t('Status', 'Tila'),
				field: 'discussionStatus',
				filter: 'agSetColumnFilter',
				cellRenderer: discussionStatusRenderer,
				valueGetter: discussionStatusGetter,
				//valueGetter: params => params.data.discussionStatus && statusMap[ params.data[params.colDef.field] ],
				filterParams: {
					values: ['OPEN', 'DONE', 'LATE'],
					valueFormatter: (params) => statusMap[params.value],
					keyCreator: (params) => params.value,
				},
			},
			{
				headerName: t('Date', 'Päivämäärä'),
				field: 'discussionDate',
				filter: 'agDateColumnFilter',
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('PersonNumber', 'Henkilönumero'),
				field: 'personNumber',
				filter: 'agNumberColumnFilter',
			},
			{
				headerName: t('Name', 'Nimi'),
				field: 'personName',
			},
			{
				headerName: t('DiscussionModel', 'Keskustelumalli'),
				field: 'discussionModel.discussionModelName',
				filter: 'agTextColumnFilter',
			},
		]

		const foundDiscussion = discussions.find((item) => item.discussionModel.discussionModelName === modelName)
		if (foundDiscussion) {
			const qasColumns = foundDiscussion.qas.flatMap((qa, idx) => [
				{
					headerName: qa.question,
					headerTooltip: qa.question,
					tooltipValueGetter: (params) => params.data.qas[idx]?.answer,
					valueGetter: (params) => params.data.qas[idx]?.answer,
				},
			])
			const evaluationsColumns = foundDiscussion.evaluations.flatMap((evaluation, idx) => [
				{
					headerName: evaluation.question,
					headerTooltip: evaluation.question,
					tooltipValueGetter: (params) => params.data.evaluations[idx]?.grade,
					valueGetter: (params) => params.data.evaluations[idx]?.grade,
				},
			])
			modelColumns.push(...qasColumns, ...evaluationsColumns)
		}

		return modelColumns
	}

	const [discussionModelOptions, setDiscussionModelOptions] = useState([])

	const fetchDiscussionModels = useCallback(async () => {
		const res = await discussionModelsGetAll()
		if (res.status === 200) {
			setDiscussionModelOptions(
				res.data.map((model) => ({
					value: model.discussionModelName,
					label: `${model.discussionModelName} (${model.discussionPrintLanguage})${model.modelValidUntil ? ` [${model.modelValidUntil}]` : ''}`,
				})),
			)
		} else {
			jsError(res)
		}
	}, [])

	useEffect(() => {
		if (!isPersonal) {
			fetchDiscussionModels()
		}
	}, [isPersonal, fetchDiscussionModels])

	const firstDataRendered = () => {
		const { api } = gridRef.current
		// set filter model and update
		api.setColumnFilterModel(filteredColumn, {
			type: '',
			filter: null,
		})
		// refresh rows based on the filter (not automatic to allow for batching multiple filters)
		api.onFilterChanged()
	}

	const discussionStatusRenderer = (params) => {
		if (!params.value) {
			return ''
		}

		return <Chip variant={statusClassMap[params.value]}>{statusMap[params.value]}</Chip>
	}

	const discussionStatusGetter = (params) => {
		if (!params.data) return
		if (params.data.discussionStatus === 'OPEN' && params.data.discussionDate < localDate()) {
			return 'LATE'
		} else {
			return params.data.discussionStatus
		}
	}

	const openNewDiscussionModal = useCallback(() => {
		dispatch({
			type: 'NEW_DISCUSSION',
			payload: {
				discussion: {
					// default values
					id: 0,
					discussionStatus: '',
					personId: '',
					personNumber: '',
					personName: '',
					discussionPrintLanguage: 'fi',
					participants: [],
					discussionType: '',
					modelValidUntil: '',
					discussionDate: '',
					discussionMemo: '',
					discussionConfidential: '',
					discussionModel: { id: 0 }, // create new discussion model
					discussionModelName: '',
					qas: [],
					evaluations: [],
					tasks: [],
				},
			},
		})
	}, [dispatch])

	const closeModal = useCallback(() => {
		dispatch({
			type: 'CLEAR_DISCUSSION',
			payload: { discussion: null },
		})
	}, [dispatch])

	const actionType = useSelector(actionTypeSelector)
	useEffect(() => {
		switch (actionType) {
			case 'POST_DISCUSSION_MODEL_SUCCEEDED':
			case 'POST_DISCUSSION_MODEL_EDIT_SUCCEEDED':
				openNewDiscussionModal()
				break

			case 'UPLOAD_DONE':
				if (isPersonal) {
					dispatch(getPersonDiscussions(personId))
				} else {
					dispatch(getAllDiscussions())
				}
				closeModal()
				break

			case 'DELETE_DISCUSSION_SUCCEEDED':
				if (isPersonal) {
					dispatch(getPersonDiscussions(personId))
				} else {
					dispatch(getAllDiscussions())
				}
				break
		}
	}, [actionType, dispatch, openNewDiscussionModal, isPersonal, personId, closeModal])

	const columnDefs = [
		{
			headerName: t('Status', 'Tila'),
			field: 'discussionStatus',
			filter: 'agSetColumnFilter',
			cellRenderer: discussionStatusRenderer,
			valueGetter: discussionStatusGetter,
			//valueGetter: params => params.data.discussionStatus && statusMap[ params.data[params.colDef.field] ],
			filterParams: {
				values: ['OPEN', 'DONE', 'LATE'],
				valueFormatter: (params) => statusMap[params.value],
				keyCreator: (params) => params.value,
			},
		},
		{
			headerName: t('PersonNumber', 'Henkilönumero'),
			field: 'personNumber',
			filter: 'agNumberColumnFilter',
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'personName',
		},
		{
			headerName: t('OtherParticipants', 'Muut osallistujat'),
			field: 'participants',
			valueGetter: (params) => params.data?.[params.colDef.field].map((participant) => participant.personName),
		},
		{
			headerName: t('DiscussionModel', 'Keskustelumalli'),
			field: 'discussionModel.discussionModelName',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: t('Date', 'Päivämäärä'),
			cellClass: 'dateType',
			field: 'discussionDate',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
	]

	const sortByModel = (modelName) => {
		const { api } = gridRef.current

		if (modelName !== null && modelName.value !== null) {
			api.setColumnFilterModel(filteredColumn, {
				type: 'equals',
				filter: modelName.value,
			})
			api.setGridOption('columnDefs', createModelColumns(modelName.value))
		} else {
			api.setColumnFilterModel(filteredColumn, null)
			api.setGridOption('columnDefs', columnDefs)
		}

		// refresh rows based on the filter (not automatic to allow for batching multiple filters)
		api.onFilterChanged()
	}

	const initialState = useMemo(
		() => ({
			columnVisibility: {
				...(isPersonal || person
					? { hiddenColIds: ['personNumber', 'personName', 'discussionModel.discussionModelName'] }
					: { hiddenColIds: [] }),
			},
		}),
		[isPersonal, person],
	)
	/*
	const removeDiscussion = (id) => {
		if (confirm(t('DeleteDiscussionConfirm', 'Haluatko varmasti poistaa keskustelun?'))) {
			dispatch(deleteDiscussion(id))
		}
	}
*/
	const doubleClickAction = (row) => {
		if (Number.isNaN(Number(row.node.id))) return
		dispatch(getDiscussion(row.data.id))
		modelSelectInputRef.current.clearValue()
	}

	const getContextMenuItems = useCallback(
		(params) => {
			const items = []

			if (!isPersonal) {
				items.push({
					name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
					action: () => {
						restoreViewState(viewStateKey, initialState)
					},
					icon: restoreIconString,
				})

				items.push('separator')
			}

			if (!params.api.isPivotMode()) {
				items.push({
					name: t('Edit', 'Muokkaa'),
					icon: pencilIconString,
					action: () => dispatch(getDiscussion(params.node.id)),
				})
				/*
      if (admin) {
        items.push(
          {
            name: t("DeleteDiscussion", "Poista keskustelu"),
            action: function () {
              removeDiscussion(params.node.id);
            },
            icon: trashIconString
          },
        );
      }
      */
				items.push('separator')
				items.push({
					name: t('Print', 'Tulosta'),
					icon: printerIconString,
					action: () => setPdf(params.node.data),
				})
			}

			items.push('export')

			return items
		},
		[isPersonal, t, initialState, dispatch],
	)

	return (
		<>
			{actionType === 'GET_DISCUSSION_LOADING' && <LoadingSuspense />}
			{pdf !== null && <PdfDiscussion data={pdf} onCancel={closeModal} setPdf={setPdf} />}

			{discussion && <DiscussionModalBody state={discussion} show handleCancel={closeModal} personId={personId} />}
			{!location.pathname.startsWith('/mgmt/persons') && (
				<Row className="my-1">
					<Col sm="4">
						<h2 className="viewheading">{t('Discussions', 'Keskustelut')}</h2>
						{!isPersonal && (
							<ReactSelect
								styles={zIndexFix}
								name="discussionModelFilter"
								onChange={sortByModel}
								ref={modelSelectInputRef}
								isClearable
								options={discussionModelOptions}
								{...reactSelectSearchSettings(t)}
							/>
						)}
					</Col>
					{!isPersonal && (
						<Col sm="8" align="right">
							<Row className="g-2">
								<Col></Col>
								<Col md="auto">
									<Button
										testId="add-discussion"
										variant="primary"
										className="addbutton"
										onClick={openNewDiscussionModal}
									>
										<IconAddNew /> {t('AddDiscussion', 'Lisää keskustelu')}
									</Button>
								</Col>
							</Row>
						</Col>
					)}
				</Row>
			)}

			<Row className="ag-theme-material styles-ag flex-grow-1">
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					sideBar={sideBar}
					rowData={discussions}
					statusBar={statusBar}
					onRowDoubleClicked={doubleClickAction}
					getRowId={(params) => String(params.data.id)}
					getLocaleText={(params) => getLocaleText(t, params)}
					getContextMenuItems={getContextMenuItems}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					onFirstDataRendered={firstDataRendered}
					initialState={isPersonal || person ? initialState : loadViewState(viewStateKey)}
					onStateUpdated={({ state }) => !isPersonal && saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
